/**
 * dynamicColorResolver.js
 * 
 * Centralized logic to resolve status colors (Red, Yellow, Green) 
 * for various finding categories based on scan data.
 */

const STATUS_COLORS = {
    red: '#dc2626',
    yellow: '#eab308',
    green: '#16a34a',
    gray: '#9ca3af'
};

/**
 * Resolve status color for a specific category
 * @param {string} category - Category name
 * @param {Object} data - Scan results data
 * @param {Object} theme - Optional theme object from ReportBuilder containing statusColors
 * @returns {string} - Hex color code
 */
export const resolveCategoryStatusColor = (category, data, theme = null) => {
    // Resolve colors with theme overrides if available
    const redColor = theme?.statusColors?.red || STATUS_COLORS.red;
    const yellowColor = theme?.statusColors?.yellow || STATUS_COLORS.yellow;
    const greenColor = theme?.statusColors?.green || STATUS_COLORS.green;
    const grayColor = STATUS_COLORS.gray;

    if (!data) return grayColor;

    // 1. Try to find in processed findings (already available in many scan results)
    // This is the most accurate way since FindingsWidgets.jsx already did the calculation
    const findings = data.findings || data.scanData?.findings || [];
    if (Array.isArray(findings)) {
        const found = findings.find(f => f[0] === category);
        if (found) {
            const status = found[1];
            if (status === 'Critical') return redColor;
            if (status === 'Warning' || status === 'At Risk') return yellowColor;
            if (status === 'Healthy') return greenColor;
            return grayColor;
        }
    }

    const normalizedCategory = category.toLowerCase().replace(/\s/g, '').replace(/\(.*\)/g, '');

    switch (normalizedCategory) {
        case 'backups':
            return getBackupsStatus(data, redColor, greenColor, grayColor);
        case 'firewall':
        case 'networkfirewall':
        case 'localhostfirewall':
            return getFirewallStatus(data, redColor, greenColor, grayColor);
        case 'services':
        case 'networkservices':
        case 'localhostservices':
            return getServicesStatus(data, yellowColor, greenColor, grayColor);
        case 'operatingsystem':
        case 'os':
            return getOSStatus(data, redColor, greenColor, grayColor);
        case 'openports':
            return getOpenPortsStatus(data, redColor, yellowColor, greenColor, grayColor);
        case 'adusers':
            return getADUsersStatus(data, redColor, greenColor, grayColor);
        case 'adcomputers':
            return getADComputersStatus(data, redColor, greenColor, grayColor);
        case 'adgroups':
            return getADGroupsStatus(data, yellowColor, greenColor, grayColor);
        case 'organizationunits':
        case 'ou':
        case 'adou':
            return getOUStatus(data, yellowColor, greenColor, grayColor);
        case 'fsmoroles':
        case 'fsmo':
            return getFSMOStatus(data, yellowColor, greenColor, grayColor);
        case 'gpopolicies':
        case 'gpo':
        case 'gpoanalysis':
            return getGPOStatus(data, redColor, yellowColor, greenColor, grayColor);
        case 'adprivilegedadmins':
        case 'adadmins':
        case 'admins':
            return getADAdminsStatus(data, yellowColor, greenColor, grayColor);
        case 'ms365users':
        case 'msusers':
            return getMS365UsersStatus(data, redColor, greenColor, grayColor);
        case 'ms365mfa':
        case 'mfa':
            return getMS365MFAStatus(data, redColor, greenColor, grayColor);
        case 'ms365policies':
        case 'mspolicy':
            return getMS365PoliciesStatus(data, yellowColor, greenColor, grayColor);
        case 'piidetection':
        case 'pii':
            return getPIIStatus(data, redColor, greenColor, grayColor);
        case 'antivirus':
            return getAntivirusStatus(data, redColor, greenColor, grayColor);
        case 'hardware':
            return getHardwareStatus(data, yellowColor, greenColor, grayColor);
        case 'softwares':
            return getSoftwaresStatus(data, yellowColor, greenColor, grayColor);
        case 'drive':
            return getDriveStatus(data, redColor, greenColor, grayColor);
        case 'age':
            return getAgeStatus(data, yellowColor, greenColor, grayColor);
        case 'encryption':
            return getEncryptionStatus(data, redColor, greenColor, grayColor);
        default:
            return grayColor;
    }
};

// --- Extraction Helpers ---

const getBackupsStatus = (data, redColor, greenColor, grayColor) => {
    const devices = data?.network_devices || data?.scanData?.network_devices || [];
    if (devices.length === 0) return grayColor;
    
    let noBackup = 0;
    let foundAnyBackupData = false;
    
    devices.forEach(d => {
        const backups = d?.system?.['backups'];
        if (!backups) return;
        foundAnyBackupData = true;
        const notes = backups['notes'] || backups['Notes'] || '';
        if (!notes.includes("Backup completed.")) noBackup++;
    });

    if (!foundAnyBackupData) return grayColor;
    return noBackup > 0 ? redColor : greenColor;
};

const getFirewallStatus = (data, redColor, greenColor, grayColor) => {
    // Check localhost first
    const localhostFirewall = data?.localhost?.["Hardware Informations"]?.Firewall;
    if (localhostFirewall && localhostFirewall.includes("Enabled")) return greenColor;
    if (localhostFirewall && localhostFirewall.includes("Disabled")) return redColor;

    // Check network devices
    const devices = data?.network_devices || data?.scanData?.network_devices || [];
    let disabled = 0;
    devices.forEach(d => {
        if (d?.system?.Firewall && d.system.Firewall.includes("Disabled")) disabled++;
    });

    return disabled > 0 ? redColor : greenColor;
};

const getServicesStatus = (data, yellowColor, greenColor, grayColor) => {
    const devices = data?.network_devices || data?.scanData?.network_devices || [];
    let riskyServices = 0;
    devices.forEach(d => {
        const services = d?.system?.['non_default_services'] || [];
        if (services.length > 5) riskyServices++; // Arbitrary threshold for "Needs Improvement"
    });
    return riskyServices > 0 ? yellowColor : greenColor;
};

const getOSStatus = (data, redColor, greenColor, grayColor) => {
    const devices = data?.network_devices || data?.scanData?.network_devices || [];
    let unsupported = 0;
    devices.forEach(d => {
        const os = d?.system?.OS || '';
        if (os.includes("2008") || os.includes("2003") || os.includes("Windows 7")) unsupported++;
    });
    return unsupported > 0 ? redColor : greenColor;
};

const getOpenPortsStatus = (data, redColor, yellowColor, greenColor, grayColor) => {
    const devices = data?.network_devices || data?.scanData?.network_devices || [];
    let hasCritical = false;
    let hasRisky = false;

    devices.forEach(d => {
        const ports = d?.system?.open_ports || [];
        ports.forEach(p => {
            if (['21', '23', '3389'].includes(p.port)) hasCritical = true;
            else if (['80', '445'].includes(p.port)) hasRisky = true;
        });
    });

    if (hasCritical) return redColor;
    if (hasRisky) return yellowColor;
    return greenColor;
};

const getADUsersStatus = (data, redColor, greenColor, grayColor) => {
    const users = data?.["Ad Scan"]?.users || data?.scanData?.["Ad Scan"]?.users || [];
    if (users.length === 0) return grayColor;
    const adminCount = users.filter(u => u.is_admin).length;
    return adminCount > users.length * 0.1 ? redColor : greenColor; // Red if >10% are admins
};

const getADComputersStatus = (data, redColor, greenColor, grayColor) => {
    const computers = data?.["Ad Scan"]?.computers || data?.scanData?.["Ad Scan"]?.computers || [];
    if (computers.length === 0) return grayColor;
    const inactive = computers.filter(c => !c.is_active).length;
    return inactive > 0 ? redColor : greenColor;
};

const getADGroupsStatus = (data) => {
    // Arbitrary healthy status for groups unless we have specific risk data
    return STATUS_COLORS.green;
};

const getOUStatus = (data, yellowColor, greenColor, grayColor) => {
    const ous = data?.["Ad Scan"]?.ous || data?.scanData?.["Ad Scan"]?.ous || [];
    if (ous.length === 0) return grayColor;
    return ous.length > 20 ? yellowColor : greenColor;
};

const getFSMOStatus = (data) => STATUS_COLORS.green;

const getGPOStatus = (data, redColor, yellowColor, greenColor, grayColor) => {
    const adData = data?.ad_data || data?.scanData?.ad_data || {};
    const issues = adData.gpo_analysis?.filter(g => g.risk === 'High')?.length || 0;
    return issues > 0 ? redColor : greenColor;
};

const getADAdminsStatus = (data, yellowColor, greenColor, grayColor) => {
    const adData = data?.ad_data || data?.scanData?.ad_data || {};
    const privileged = adData.privileged_admins?.length || 0;
    return privileged > 10 ? yellowColor : greenColor;
};

// --- MS365 Helpers ---
const getMS365UsersStatus = (data, redColor, greenColor, grayColor) => {
    const report = data?.MS365?.report || data?.scanData?.MS365?.report || {};
    const total = report['Total Users'] || 0;
    const noMfa = report['Users Without MFA'] || 0;
    if (total === 0) return grayColor;
    return (noMfa / total) > 0.1 ? redColor : greenColor; // Red if >10% missing MFA
};

const getMS365MFAStatus = (data, redColor, greenColor, grayColor) => {
    const msData = data?.ms365_data || data?.scanData?.ms365_data || {};
    const withoutMfa = msData.users_without_mfa?.length || 0;
    return withoutMfa > 0 ? redColor : greenColor;
};

const getMS365PoliciesStatus = (data, yellowColor, greenColor, grayColor) => {
    const policies = data?.MS365?.policies || data?.scanData?.MS365?.policies || [];
    if (policies.length === 0) return grayColor;
    
    let hasIssues = false;
    policies.forEach(p => {
        if (p.Status && p.Status.toLowerCase() !== 'enabled') hasIssues = true;
    });

    return hasIssues ? yellowColor : greenColor;
};

const getPIIStatus = (data, redColor, greenColor, grayColor) => {
    const piiData = data?.pii_data || data?.scanData?.pii_data || [];
    return piiData.length > 0 ? redColor : greenColor;
};

// --- Localhost Specific Helpers ---
const getAntivirusStatus = (data, redColor, greenColor, grayColor) => {
    const av = data?.localhost?.["Hardware Informations"]?.Antivirus;
    if (!av) return grayColor;
    return av.toLowerCase().includes("defender") ? greenColor : redColor; // Simplistic check
};

const getHardwareStatus = (data, yellowColor, greenColor, grayColor) => STATUS_COLORS.green;

const getSoftwaresStatus = (data, yellowColor, greenColor, grayColor) => {
    const devices = data?.network_devices || data?.scanData?.network_devices || [];
    let highRisk = 0;
    devices.forEach(d => {
        const softwares = d?.system?.['softwares'] || [];
        if (softwares.length > 20) highRisk++; // Arbitrary
    });
    return highRisk > 0 ? yellowColor : greenColor;
};

const getDriveStatus = (data, redColor, greenColor, grayColor) => {
    const devices = data?.network_devices || data?.scanData?.network_devices || [];
    let lowSpace = 0;
    devices.forEach(d => {
        const drives = d?.system?.['drive'] || [];
        drives.forEach(drive => {
            const free = parseFloat(drive[2]); // Free %
            if (free < 10) lowSpace++;
        });
    });
    return lowSpace > 0 ? redColor : greenColor;
};

const getAgeStatus = (data, yellowColor, greenColor, grayColor) => {
    const devices = data?.network_devices || data?.scanData?.network_devices || [];
    let oldDevices = 0;
    devices.forEach(d => {
        const age = parseInt(d?.system?.['age'] || 0);
        if (age > 5) oldDevices++;
    });
    return oldDevices > 0 ? yellowColor : greenColor;
};

const getEncryptionStatus = (data, redColor, greenColor, grayColor) => {
    const drives = data?.localhost?.["Drive Informations"] || [];
    if (drives.length === 0) return grayColor;
    
    let unencrypted = false;
    drives.forEach(d => {
        if (!d.encrypted) unencrypted = true;
    });
    return unencrypted ? redColor : greenColor;
};
